/**
 * @license
 * @copyright Copyright Motili Inc., 2024 All Rights Reserved
 */

import {
    useEffect,
    useState,
    CSSProperties,
    ReactNode,
    Ref,
    ChangeEvent,
    ChangeEventHandler,
    FocusEvent,
    FocusEventHandler,
    FormEventHandler,
    KeyboardEventHandler,
} from 'react';
import FormControl from 'react-bootstrap/FormControl';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import classNames from 'classnames';

import { Validation } from 'common/components/form/FormValidationDisplay';
import { getValidationState } from 'common/utils/validation-utils';

import {
    LabelWithRequired,
    resolveComponentLocator,
    joinDataTestId,
    format,
    getMaskConfig,
    type MaskType,
} from '@motili/ui';
import Config from 'Config';
import styles from './FormTextInput.module.scss';
import { StyledInput } from './StyledInput';
import { StyledInputMask } from './StyledInputMask';

export interface FormTextInputProps {
    style?: CSSProperties;
    feedbackStyle?: CSSProperties;
    labelStyle?: CSSProperties;
    className?: string;
    controlId?: string;
    name?: string;
    label?: string;
    placeholder?: string;
    validation?: Validation;
    required?: boolean;
    mask?: MaskType;
    disabled?: boolean;
    inputGroupBefore?: ReactNode;
    inputGroupAfter?: ReactNode;
    saveUnmasked?: boolean;
    maxLength?: number;
    value?: string | number | readonly string[];
    type?: string;
    autocomplete?: string;
    dataTestId?: string;
    onChange?:
        | ChangeEventHandler<HTMLInputElement>
        | FormEventHandler<typeof FormControl>
        | undefined;
    autoFocus?: boolean;
    onBlur?:
        | FocusEventHandler<typeof FormControl>
        | FocusEventHandler<HTMLInputElement>;

    onKeyPress?:
        | KeyboardEventHandler<HTMLInputElement>
        | KeyboardEventHandler<typeof FormControl>;

    unitSuffix?: ReactNode;
    onFocus?: FocusEventHandler<typeof FormControl>;
    ref?: Ref<HTMLInputElement>;
}

const FormTextInput = ({
    style = {},
    feedbackStyle = {},
    className = '',
    validation = { state: null },
    saveUnmasked = false,
    ref,
    controlId,
    name,
    label,
    labelStyle,
    required,
    dataTestId,
    mask,
    value,
    autoFocus,
    disabled,
    placeholder,
    onChange,
    onKeyPress,
    onBlur,
    onFocus,
    maxLength,
    type,
    autocomplete,
    inputGroupBefore,
    inputGroupAfter,
    unitSuffix,
}: FormTextInputProps) => {
    const prefixTestId = dataTestId || controlId || name || 'form-text-input';

    const inputPrefixTestId = joinDataTestId([
        prefixTestId,
        resolveComponentLocator('', 'input', prefixTestId),
    ]);

    const { isInvalid, isValid, validationFeedbackType } =
        getValidationState(validation);

    return (
        <Form.Group
            style={style}
            className={className}
            controlId={controlId || name}
        >
            {label && (
                <LabelWithRequired
                    dataTestId={prefixTestId}
                    style={labelStyle || undefined}
                    label={label}
                    required={required}
                />
            )}
            <InputGroupConditionalWrapper
                inputGroupAfter={inputGroupAfter}
                inputGroupBefore={inputGroupBefore}
                unitSuffix={unitSuffix}
            >
                {inputGroupBefore}
                {!mask && (
                    <FormControl
                        inputRef={
                            ref as
                                | ((instance: HTMLInputElement) => void)
                                | undefined
                        }
                        value={value}
                        autoFocus={autoFocus}
                        disabled={disabled}
                        placeholder={placeholder}
                        onChange={
                            onChange as
                                | FormEventHandler<FormControl>
                                | undefined
                        }
                        onKeyPress={
                            onKeyPress as
                                | KeyboardEventHandler<FormControl>
                                | undefined
                        }
                        onBlur={
                            onBlur as FocusEventHandler<FormControl> | undefined
                        }
                        maxLength={maxLength}
                        name={name}
                        type={type}
                        autoComplete={autocomplete}
                        data-test-id={inputPrefixTestId}
                        as={StyledInput}
                        onFocus={onFocus}
                        isValid={isValid}
                        isInvalid={isInvalid}
                    />
                )}
                {mask && (
                    <MaskedInput
                        mask={mask}
                        saveUnmasked={saveUnmasked}
                        inputPrefixTestId={inputPrefixTestId}
                        isValid={isValid}
                        isInvalid={isInvalid}
                        ref={ref}
                        autoFocus={autoFocus}
                        disabled={disabled}
                        placeholder={placeholder}
                        value={value}
                        name={name}
                        type={type}
                        onChange={
                            onChange as
                                | ChangeEventHandler<HTMLInputElement>
                                | undefined
                        }
                        onBlur={
                            onBlur as
                                | FocusEventHandler<HTMLInputElement>
                                | undefined
                        }
                        onKeyPress={
                            onKeyPress as
                                | KeyboardEventHandler<HTMLInputElement>
                                | undefined
                        }
                    />
                )}
                {inputGroupAfter}
                {unitSuffix && (
                    <span style={{ marginLeft: '10px' }}>{unitSuffix}</span>
                )}
            </InputGroupConditionalWrapper>
            {isInvalid && validation?.message && (
                <Form.Control.Feedback
                    type={validationFeedbackType}
                    style={{ display: 'block', ...feedbackStyle }}
                >
                    {validation.message}
                </Form.Control.Feedback>
            )}
        </Form.Group>
    );
};

export default FormTextInput;

interface MaskedInputProps {
    mask: MaskType;
    saveUnmasked: boolean;
    inputPrefixTestId: string;
    isValid: boolean;
    isInvalid: boolean;
    autoFocus?: boolean;
    disabled?: boolean;
    placeholder?: string;
    value?: string | number | readonly string[];
    name?: string;
    type?: string;
    onChange?: ChangeEventHandler<HTMLInputElement>;
    onBlur?: FocusEventHandler<HTMLInputElement>;
    onKeyPress?: KeyboardEventHandler<HTMLInputElement>;
    ref?: Ref<HTMLInputElement>;
}

const MaskedInput = ({
    mask,
    saveUnmasked,
    inputPrefixTestId,
    isValid,
    isInvalid,
    autoFocus,
    disabled,
    placeholder,
    value,
    name,
    type,
    onChange,
    onBlur,
    onKeyPress,
    ref,
}: MaskedInputProps) => {
    const maskConfig = getMaskConfig(mask);
    const showMaskOnFocusOnly = !!maskConfig.showMaskOnFocusOnly;

    const [isFocused, setIsFocused] = useState(false);
    const [hasContent, setHasContent] = useState(
        /\S/.test(String(value ?? ''))
    );

    useEffect(() => {
        setHasContent(/\S/.test(String(value ?? '')));
    }, [value]);

    const showMask = showMaskOnFocusOnly ? isFocused || hasContent : true;
    const remountKey = showMaskOnFocusOnly && showMask ? 'masked' : 'unmasked';

    return (
        <StyledInputMask
            key={showMaskOnFocusOnly ? remountKey : undefined}
            className={classNames({
                'form-control': true,
                [styles.input]: disabled,
                'is-valid': isValid,
                'is-invalid': isInvalid,
            })}
            style={
                !hasContent && !isFocused
                    ? { color: Config.color.darkGray2 }
                    : undefined
            }
            data-test-id={inputPrefixTestId}
            ref={ref}
            autoFocus={showMaskOnFocusOnly ? isFocused : autoFocus}
            value={format(String(value ?? ''), maskConfig)}
            disabled={disabled}
            placeholder={placeholder}
            saveUnmasked={saveUnmasked}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setHasContent(!!e.target.value);
                if (onChange) {
                    onChange(e);
                }
            }}
            onKeyPress={onKeyPress}
            onFocus={() => {
                setIsFocused(true);
            }}
            onBlur={(e: FocusEvent<HTMLInputElement>) => {
                setIsFocused(false);
                if (onBlur) {
                    onBlur(e);
                }
            }}
            mask={maskConfig.mask}
            replacement={maskConfig.replacement}
            showMask={showMask}
            name={name}
            type={type}
        />
    );
};

function InputGroupConditionalWrapper(props: {
    inputGroupBefore: ReactNode | undefined;
    inputGroupAfter: ReactNode | undefined;
    children: ReactNode | undefined;
    unitSuffix: ReactNode | undefined;
}) {
    if (props.inputGroupBefore || props.inputGroupAfter) {
        return (
            <InputGroup style={{ width: '100%' }}>{props.children}</InputGroup>
        );
    }
    if (props.unitSuffix) {
        return (
            <span style={{ display: 'flex', alignItems: 'baseline' }}>
                {props.children}
            </span>
        );
    }
    return <span>{props.children}</span>;
}
